import { useState } from 'react';
import { toast } from 'react-toastify';

/**
 * Two-step phone verifier: request a code, then confirm it in a modal.
 * Failed code confirmations count toward `maxAttempts` when provided.
 */
export const usePhoneVerifier = ({
  initiate,
  confirm,
  maxAttempts = null,
  initialAttempts = 0
} = {}) => {
  const [verifiedValue, setVerifiedValue] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [showModal, setShowModal] = useState(false);
  // Seeded from the agency record's verification_attempts so the count (and lock)
  // survives a reload; in-session failures increment it locally.
  const [attempts, setAttempts] = useState(initialAttempts);

  const locked = maxAttempts != null && attempts >= maxAttempts;
  const attemptsLeft = maxAttempts != null ? Math.max(0, maxAttempts - attempts) : null;

  const request = async (value) => {
    if (locked) {
      toast.error('Maximum verification attempts reached. Please contact support.');
      return;
    }

    setRequesting(true);
    const { error } = await initiate(value);
    setRequesting(false);

    if (error) {
      toast.error('Could not send a verification code. Please try again.');
      return;
    }
    setShowModal(true);
  };

  const submitCode = async (value, code) => {
    setConfirming(true);
    const { error } = await confirm(value, code);
    setConfirming(false);

    if (error) {
      setAttempts((n) => n + 1);
      toast.error('That code is invalid or has expired.');
      return;
    }

    setVerifiedValue(value);
    setShowModal(false);
    toast.success('Phone number verified.');
  };

  const isVerified = (value) => !!verifiedValue && verifiedValue === value;

  return {
    requesting,
    confirming,
    showModal,
    setShowModal,
    attempts,
    attemptsLeft,
    locked,
    request,
    submitCode,
    isVerified,
    setVerifiedValue
  };
};
